<template>
  <section class="self-service-page" id="self-service-page">
    <div class="bg-shape bg-shape-one"></div>
    <div class="bg-shape bg-shape-two"></div>

    <Navbar />

    <main class="admin-main self-service-main">
      <div class="self-service-shell">
        <header class="service-hero">
          <p class="eyebrow">Support Services</p>
          <h1>
            <i class="fe fe-grid m-r-10"></i>
            Self Service
          </h1>
          <p class="subtitle">Select any service below to continue.</p>
        </header>

        <div class="cover-banner" v-if="me" :class="'cover-' + coverStatus.key">
          <div>
            <p class="eyebrow mb-1">{{ me.firstname }} {{ me.lastname }}<span v-if="me.id_card_number"> · {{ me.id_card_number }}</span></p>
            <h4 class="mb-1">{{ coverStatus.title }}</h4>
            <p class="mb-0 small">{{ coverStatus.text }}</p>
          </div>
          <router-link v-if="renewPath" :to="renewPath" class="btn btn-renew">
            <i class="fe fe-credit-card mr-1"></i> {{ coverStatus.key === "active" ? "Renew early" : "Renew now" }}
          </router-link>
        </div>

        <section>
          <div class="container px-0">
            <div class="row">
              <div class="col-lg-3 col-md-6 my-3" v-for="(card, index) in cards" :key="`self-service-${index}`">
                <div class="service-card m-b-30 h-100">
                  <div class="card-body d-flex flex-column">
                    <div class="avatar avatar-lg m-b-15">
                      <div class="avatar-title bg-soft-primary rounded-circle">
                        <i :class="card.icon"></i>
                      </div>
                    </div>

                    <h5 class="m-b-20">{{ card.title }}</h5>

                    <div class="m-t-auto">
                      <router-link v-if="card.path" :to="card.path" class="btn action-btn btn-sm btn-block">
                        Open
                      </router-link>
                      <button v-else type="button" class="btn btn-outline-secondary btn-sm btn-block" disabled>
                        Coming Soon
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";

  export default {
    components: {
      Navbar,
    },
    data() {
      return {
        agency: null,
        user: null,
        me: null,
        cards: [],
      };
    },
    computed: {
      renewPath() {
        if (!this.user) return null;
        const agencyId = this.agency ? this.agency.id : "";
        return this.firstRoute([`/subscribe/${agencyId}/${this.user.id}`, `/subscribe/${this.user.id}`, `/subscription-renewal`]);
      },
      coverStatus() {
        const expiry = this.me && this.me.expiry_date ? new Date(String(this.me.expiry_date).substr(0, 10)) : null;
        if (!expiry || isNaN(expiry)) return { key: "unknown", title: "Your cover", text: "We could not find an expiry date for your cover." };
        const days = Math.ceil((expiry - new Date(new Date().toDateString())) / 86400000);
        const date = expiry.toDateString();
        if (days < 0) return { key: "expired", title: "Your cover has expired", text: `It expired on ${date}. Renew to access care again.` };
        if (days <= 30) return { key: "expiring", title: `Your cover expires in ${days} day${days === 1 ? "" : "s"}`, text: `It ends on ${date}. Renew now so your care isn't interrupted.` };
        return { key: "active", title: "Your cover is active", text: `It runs until ${date}.` };
      },
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user") || "null") || {};
      this.agency = JSON.parse(localStorage.getItem("agency") || "null");

      const userId = this.user.id ? this.user.id : null;
      const agencyId = this.agency ? this.agency.id : null;
      const ticket = this.firstRoute([agencyId ? `/add-complaint/${agencyId}` : null, "/add-complaint"]);

      this.cards = [
        { title: "Renew My Cover (Pay Online)", icon: "fe fe-credit-card", path: this.renewPath },
        { title: "Download My ID Card", icon: "fe fe-credit-card", path: userId ? this.firstRoute([`/id_card/${userId}`, `/enrollee_slip/${userId}`]) : null },
        { title: "Request for Change of Facility", icon: "fe fe-repeat", path: this.firstRoute(["/transfer-of-hcp"]) || ticket },
        { title: "Submit a Ticket", icon: "fe fe-clock", path: ticket },
        { title: "Request for Change of HMO", icon: "fe fe-briefcase", path: ticket },
        { title: "Edit Personal Details", icon: "fe fe-user", path: userId ? this.firstRoute([`/edit-user/${userId}`]) : null },
        { title: "Add/Update Dependent", icon: "fe fe-users", path: userId ? this.firstRoute([`/add-dependent/${userId}`]) : null },
        { title: "Request for out of state Treatment", icon: "fe fe-map-pin", path: this.firstRoute(["/request-corridor"]) },
        { title: "Print Slip", icon: "fe fe-printer", path: userId ? this.firstRoute([`/enrollee_slip/${userId}`]) : null },
        { title: "Request Authorization Code", icon: "fe fe-lock", path: null },
      ];

      // Fresh expiry date for the cover banner.
      this.axios
        .get("/api/v1/auth/user")
        .then((res) => { this.me = (res.data && (res.data.user || res.data.data || res.data)) || null; })
        .catch(() => { this.me = this.user; });
    },
    methods: {
      /** The first of these paths that this app has a route for (the frontends differ). */
      firstRoute(paths) {
        for (const path of paths) {
          const matched = path ? this.$router.resolve(path).route.matched : [];
          if (matched.length && matched[0].path !== "*") return path;
        }
        return null;
      },
    },
  };
</script>

<style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap");

  .self-service-page {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    font-family: "Space Grotesk", sans-serif;
    background:
      radial-gradient(circle at 10% 15%, rgba(17, 105, 71, 0.15), transparent 45%),
      radial-gradient(circle at 90% 85%, rgba(0, 122, 204, 0.2), transparent 42%),
      linear-gradient(140deg, #f5f8f7 0%, #eef5ff 100%);
  }

  .cover-banner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; border-radius: 16px; padding: 18px 22px; margin-bottom: 24px; color: #fff; }
  .cover-active { background: linear-gradient(135deg, #065f46, #10b981); }
  .cover-expiring { background: linear-gradient(135deg, #92400e, #f59e0b); }
  .cover-expired { background: linear-gradient(135deg, #7f1d1d, #ef4444); }
  .cover-unknown { background: linear-gradient(135deg, #1e3a5f, #2563eb); }
  .cover-banner .eyebrow { opacity: .85; color: #fff; }
  .btn-renew { background: #fff; color: #0f172a; font-weight: 700; border-radius: 10px; padding: 8px 18px; }

  .self-service-main {
    position: relative;
    z-index: 1;
    padding: 40px 20px;
  }

  .self-service-shell {
    max-width: 1100px;
    margin: 0 auto;
  }

  .bg-shape {
    position: absolute;
    z-index: 0;
    border-radius: 50%;
    filter: blur(2px);
  }

  .bg-shape-one {
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    background: rgba(0, 122, 204, 0.13);
  }

  .bg-shape-two {
    bottom: -100px;
    left: -100px;
    width: 280px;
    height: 280px;
    background: rgba(17, 105, 71, 0.16);
  }

  .service-hero {
    text-align: center;
    margin-bottom: 26px;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #116947;
    font-weight: 700;
    font-size: 0.75rem;
    margin-bottom: 8px;
  }

  .service-hero h1 {
    margin: 0;
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    color: #102a43;
    line-height: 1.1;
  }

  .subtitle {
    max-width: 620px;
    margin: 12px auto 0;
    color: #334e68;
    font-size: 1rem;
  }

  .service-card {
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(16, 42, 67, 0.08);
    border-radius: 20px;
    box-shadow: 0 18px 40px rgba(16, 42, 67, 0.12);
    backdrop-filter: blur(6px);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .service-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 22px 44px rgba(16, 42, 67, 0.16);
  }

  .service-card h5 {
    color: #102a43;
    font-weight: 700;
  }

  .service-card .card-body {
    align-items: center;
    text-align: center;
  }

  .service-card .m-t-auto {
    width: 100%;
  }

  .avatar-title {
    background: linear-gradient(120deg, rgba(17, 105, 71, 0.14) 0%, rgba(0, 122, 204, 0.2) 100%);
    color: #116947;
  }

  .action-btn {
    border: 0;
    border-radius: 12px;
    background: linear-gradient(120deg, #116947 0%, #007acc 100%);
    color: #fff;
    font-weight: 700;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .action-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 122, 204, 0.25);
    color: #fff;
  }

  @media (max-width: 640px) {
    .self-service-main {
      padding: 24px 14px;
    }
  }
</style>